// Shared presentation taxonomy for logs emitted by all four APIs.
// Raw `origem` and `type` identifiers are preserved for technical diagnosis;
// table labels and filters use the compact categories below.

export const LOG_LEVELS = {
  error: { label: 'Erro', badge: 'danger', description: 'Falha operacional ou técnica.' },
  warn: { label: 'Aviso', badge: 'warning', description: 'Bloqueio controlado, validação ou condição recuperável.' },
  info: { label: 'Informação', badge: 'info', description: 'Evento informacional esperado.' },
  success: { label: 'Sucesso', badge: 'success', description: 'Operação concluída com sucesso.' },
};

export const LOG_PROJECTS = {
  0: { label: 'Todos os projetos' },
  1: { label: 'Plataforma de aplicativos' },
  2: { label: 'Plataforma de eventos' },
  3: { label: 'Painel / Central' },
  4: { label: 'Credenciamento' },
};

export const LOG_PROJECT_OPTIONS = Object.entries(LOG_PROJECTS).map(([value, meta]) => ({
  value: Number(value),
  label: meta.label,
}));

const CATEGORY_FILTER_PREFIX = 'category:';

// Rule order is significant: the first match wins. Keep these codes and patterns
// synchronized with server/log-taxonomy.js in flex-interativa-server.
export const LOG_ORIGIN_CATEGORIES = [
  {
    value: 'observability',
    label: 'Observabilidade',
    group: 'Plataforma',
    pattern: '(http[-_]?terminal|http[-_]?outcome|client[-_]?telemetry|clienttelemetry|global[-_]?error|health[-_]?check|healthcheck|getlogs|handlegetlogs|logqueue|applicationlog|marklogviewed|deletelogs|globalerrorlogs|logs$)',
    description: 'Instrumentação HTTP, telemetria do navegador, saúde e administração dos próprios logs.',
  },
  {
    value: 'security',
    label: 'Segurança e acesso',
    group: 'Plataforma',
    pattern: '(verify|auth|login|logout|signin|password|recovery|token|permission|authorize|blockrequest)',
    description: 'Autenticação, autorização, sessão, token e proteção de acesso.',
  },
  {
    value: 'streaming',
    label: 'Streaming',
    group: 'Negócio',
    pattern: '(stream|playback|audience|viewer)',
    description: 'Transmissões, audiência, player, sessões e interações em tempo real.',
  },
  {
    value: 'credentialing',
    label: 'Credenciamento',
    group: 'Negócio',
    pattern: '(credential|checkin|rfid|qrcode|qr[-_]?code|scan)',
    description: 'Credenciamento, check-in, RFID e leitura de QR Code.',
  },
  {
    value: 'communication',
    label: 'Comunicação',
    group: 'Negócio',
    pattern: '(email|ses|notification|webpush|pushsubscription|message|chat|announcement)',
    description: 'E-mail, notificações, push, chat e anúncios.',
  },
  {
    value: 'analytics',
    label: 'Analytics e pontuação',
    group: 'Negócio',
    pattern: '(analytics|tracking|score|report|metric|activityhistory|historyactivity|achievement|dashboard|credit)',
    description: 'Analytics, relatórios, métricas, histórico e pontuação.',
  },
  {
    value: 'files-ai',
    label: 'Arquivos, mídia e IA',
    group: 'Plataforma',
    pattern: '(upload|file|media|image|thumbnail|asset|generation|falai|fal[-_]|directory|download|aimodel|googlefont)',
    description: 'Uploads, arquivos, mídia, geração de conteúdo e provedores de IA.',
  },
  {
    value: 'integrations',
    label: 'Integrações externas',
    group: 'Plataforma',
    pattern: '(sympla|webhook|external|proxy|sns)',
    description: 'Webhooks e integrações com serviços externos.',
  },
  {
    value: 'participants',
    label: 'Participantes e social',
    group: 'Negócio',
    pattern: '(user|participant|visitor|friend|network|social|lead|contact|rsvp|profile|perfil|newsfeed|subscribe)',
    description: 'Participantes, usuários, networking, contatos e recursos sociais.',
  },
  {
    value: 'events-content',
    label: 'Eventos e conteúdo',
    group: 'Negócio',
    pattern: '(event|component|schedule|sponsor|exhibitor|activit|quiz|restaurant|gallery|customization|customizacao|template|project|application|manifest|sector|speaker|competition|order|interestpoint)',
    description: 'Eventos, agenda, atividades, expositores, conteúdo e configurações relacionadas.',
  },
  {
    value: 'background',
    label: 'Rotinas internas',
    group: 'Plataforma',
    pattern: '(manager|cron|cleanup|reconcile|initialize|heartbeat|scheduler)',
    description: 'Rotinas agendadas, reconciliação, inicialização e manutenção interna.',
  },
  {
    value: 'api-middleware',
    label: 'API e middleware',
    group: 'Plataforma',
    pattern: '(middleware|endpoint|request|handler|seo)',
    description: 'Endpoints, middleware e processamento HTTP genérico.',
  },
  {
    value: 'system',
    label: 'Sistema',
    group: 'Plataforma',
    pattern: null,
    description: 'Origem técnica que não pertence a um domínio mais específico.',
  },
];

export const LOG_OPERATION_TYPE_CATEGORIES = [
  {
    value: 'response',
    label: 'Resposta / resultado',
    badge: 'success',
    pattern: '(response|terminal[-_]?outcome|^(get|post|put|patch|delete)-/)',
    description: 'Resposta ou resultado terminal de uma operação.',
    affectedArea: 'API / Fluxo terminal',
  },
  {
    value: 'input',
    label: 'Entrada',
    badge: 'info',
    pattern: '(input|received|batch[-_]?start|[_-]start$)',
    description: 'Entrada recebida ou início explícito de processamento.',
    affectedArea: 'Entrada de dados',
  },
  {
    value: 'database',
    label: 'Banco de dados',
    badge: 'danger',
    pattern: '(query|database|db[-_]|transaction|rollback|schema|sql|persist|log[-_]?insert|update[_-]?batch|update[_-]?email)',
    description: 'Consulta, persistência, transação ou compatibilidade de schema.',
    affectedArea: 'Banco de dados',
  },
  {
    value: 'authentication',
    label: 'Autenticação / acesso',
    badge: 'warning',
    pattern: '(auth|token|login|permission)',
    description: 'Autenticação, autorização ou sessão.',
    affectedArea: 'Segurança',
  },
  {
    value: 'validation',
    label: 'Validação / bloqueio',
    badge: 'warning',
    pattern: '(validation|invalid|missing|not[-_]?found|block|reject|limit|mismatch|duplicate|duplication|expired|expiration|unavailable|conflict|check[-_]?error|no[-_]?recipients)',
    description: 'Validação, bloqueio de negócio ou estado indisponível.',
    affectedArea: 'Regras de negócio',
  },
  {
    value: 'files',
    label: 'Arquivo / mídia',
    badge: 'info',
    pattern: '(upload|file|image|thumbnail|media|asset|generation)',
    description: 'Processamento, validação ou armazenamento de arquivo e mídia.',
    affectedArea: 'Arquivos e mídia',
  },
  {
    value: 'integration',
    label: 'Integração externa',
    badge: 'info',
    pattern: '(external|sympla|ses|sns|provider|webhook|fal|url[-_]?fetch)',
    description: 'Chamada, retorno ou falha de provedor externo.',
    affectedArea: 'Integrações',
  },
  {
    value: 'communication',
    label: 'Comunicação',
    badge: 'info',
    pattern: '(email|notification|webpush|push|chat|suppress)',
    description: 'E-mail, notificação, push ou chat.',
    affectedArea: 'Comunicação',
  },
  {
    value: 'analytics',
    label: 'Analytics / telemetria',
    badge: 'info',
    pattern: '(analytics|tracking|viewer|telemetry|score)',
    description: 'Analytics, telemetria, audiência ou pontuação.',
    affectedArea: 'Analytics',
  },
  {
    value: 'summary',
    label: 'Resumo de rotina',
    badge: 'secondary',
    pattern: '(summary|cleanup|reconcile|health|batch)',
    description: 'Resumo consolidado de rotina ou verificação de saúde.',
    affectedArea: 'Rotinas internas',
  },
  {
    value: 'completion',
    label: 'Conclusão / auditoria',
    badge: 'success',
    pattern: '(success|sent|create|update|delete|add|remove|clear|join|left|leave|track|mark|hide|generated|finalization|finalize|complete|award|revoke|accept)',
    description: 'Conclusão registrada para auditoria ou acompanhamento operacional.',
    affectedArea: 'Auditoria',
  },
  {
    value: 'processing',
    label: 'Processamento',
    badge: 'secondary',
    pattern: null,
    description: 'Etapa interna ou tipo técnico sem uma classe mais específica.',
    affectedArea: 'Sistema',
  },
];

function categoryMetadata(rules) {
  return Object.fromEntries(rules.map(rule => [
    `${CATEGORY_FILTER_PREFIX}${rule.value}`,
    {
      label: rule.label,
      category: rule.group,
      description: rule.description,
      affectedArea: rule.affectedArea,
    },
  ]));
}

const SHARED_METADATA = {
  types: categoryMetadata(LOG_OPERATION_TYPE_CATEGORIES),
  origins: categoryMetadata(LOG_ORIGIN_CATEGORIES),
};

export const LOG_METADATA = {
  1: SHARED_METADATA,
  2: SHARED_METADATA,
  3: SHARED_METADATA,
  4: SHARED_METADATA,
};

function normalizeProjectId(projectId) {
  const parsed = Number(projectId);
  return Object.prototype.hasOwnProperty.call(LOG_PROJECTS, parsed) ? parsed : 0;
}

function humanizeLogKey(value, fallback) {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, char => char.toUpperCase());
}

function findCategory(rules, rawValue) {
  const value = String(rawValue || '');
  return rules.find(rule => !rule.pattern || new RegExp(rule.pattern, 'i').test(value));
}

export function getProjectName(value) {
  const projectId = normalizeProjectId(value);
  return LOG_PROJECTS[projectId]?.label || '';
}

export function getTypeInfo(projectId, typeValue) {
  const rawType = typeValue === null || typeValue === undefined ? '' : String(typeValue);
  const rule = findCategory(LOG_OPERATION_TYPE_CATEGORIES, rawType);
  return {
    label: rule.label,
    badge: rule.badge,
    description: rule.description,
    affectedArea: rule.affectedArea || getProjectName(projectId) || 'Sistema',
    category: rule.value,
    rawValue: rawType,
  };
}

export function getOriginInfo(projectId, originValue) {
  const rawOrigin = originValue === null || originValue === undefined ? '' : String(originValue);
  const rule = findCategory(LOG_ORIGIN_CATEGORIES, rawOrigin);
  return {
    label: rule.label,
    category: rule.label,
    categoryValue: rule.value,
    file: null,
    route: null,
    description: rule.description,
    project: normalizeProjectId(projectId),
    rawValue: rawOrigin,
  };
}

export function getLevelInfo(level) {
  return LOG_LEVELS[level] || {
    label: humanizeLogKey(level, 'Sem nível'),
    badge: 'secondary',
    description: '',
  };
}

export function getOriginsByCategory() {
  return LOG_ORIGIN_CATEGORIES.reduce((grouped, rule) => {
    if (!grouped[rule.group]) grouped[rule.group] = [];
    grouped[rule.group].push({
      value: `${CATEGORY_FILTER_PREFIX}${rule.value}`,
      label: rule.label,
      description: rule.description,
    });
    return grouped;
  }, {});
}

export function getAllTypes() {
  return LOG_OPERATION_TYPE_CATEGORIES.map(rule => ({
    value: `${CATEGORY_FILTER_PREFIX}${rule.value}`,
    label: rule.label,
    description: rule.description,
  }));
}

export function getLogFilterParams(originValue = '', typeValue = '') {
  const origin = String(originValue || '');
  const type = String(typeValue || '');
  const originIsCategory = origin.startsWith(CATEGORY_FILTER_PREFIX);
  const typeIsCategory = type.startsWith(CATEGORY_FILTER_PREFIX);
  return {
    origem: originIsCategory ? '' : origin,
    origem_categoria: originIsCategory ? origin.slice(CATEGORY_FILTER_PREFIX.length) : '',
    type: typeIsCategory ? '' : type,
    type_categoria: typeIsCategory ? type.slice(CATEGORY_FILTER_PREFIX.length) : '',
  };
}

function safeStringify(value) {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value ?? '');
  }
}

function getBoundedLogJson(value, maximum = 200) {
  const serialized = safeStringify(value);
  if (serialized.length <= maximum) return serialized;
  return `${serialized.slice(0, Math.max(0, maximum - 3))}...`;
}

export function parseLogPayload(value) {
  let parsed = value;
  for (let i = 0; i < 3; i += 1) {
    if (typeof parsed !== 'string') break;
    const trimmed = parsed.trim();
    if (!trimmed) return '';
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return parsed;
    }
  }
  return parsed;
}

function getPrimitivePreview(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return safeStringify(value);
}

export function getLogPreview(payload) {
  if (!payload) return 'Sem payload';
  if (typeof payload !== 'object') return getPrimitivePreview(payload);
  const candidates = [
    payload.message,
    payload.error?.message,
    payload.error,
    payload.sqlMessage,
    payload.description,
    payload.action,
    payload.scope,
    payload.url,
    payload.request?.url,
    payload.response?.message,
  ];
  const preview = candidates.map(getPrimitivePreview).find(Boolean) || safeStringify(payload);
  return preview.length > 500 ? `${preview.slice(0, 497)}...` : preview;
}

function getUserEmail(row, parsedUserInfo) {
  const email = row.user_email || parsedUserInfo?.email;
  if (typeof email === 'string') return email;
  if (email?.value) return email.value;
  return '';
}

export function normalizeLogRow(row = {}, options = {}) {
  const projectId = normalizeProjectId(row.projeto);
  const rawType = row.rawType ?? row.type ?? '';
  const rawOrigem = row.rawOrigem ?? row.origem ?? '';
  const typeInfo = getTypeInfo(projectId, rawType);
  const originInfo = getOriginInfo(projectId, rawOrigem);
  const parsedPayload = parseLogPayload(row.erro);
  const parsedUserInfo = parseLogPayload(row.user_info);
  const operationId = row.operation_id || parsedPayload?.operation_id || '';
  const displayDate = typeof options.convertDate === 'function' && row.data
    ? options.convertDate(row.data)
    : row.data;

  return {
    ...row,
    projeto: projectId,
    projectLabel: getProjectName(projectId),
    rawType,
    typeLabel: typeInfo.label,
    typeCategory: typeInfo.category,
    type: typeInfo.label,
    rawOrigem,
    originLabel: originInfo.label,
    originCategory: originInfo.categoryValue,
    origem: originInfo.label,
    rawErro: safeStringify(row.erro),
    parsedPayload,
    erro: parsedPayload,
    json: getBoundedLogJson(parsedPayload),
    operationId,
    user_info: row.user_info,
    parsedUserInfo,
    user_email: getUserEmail(row, parsedUserInfo),
    viewed: Number(row.viewed || 0),
    data: displayDate,
  };
}
